import { ChevronDownIcon } from '@chakra-ui/icons';
import { MenuButton, Button, Text } from '@chakra-ui/react';

import { capitalizeFirstLetter } from 'utils/GlobalHelpers';
import { accent, primaryAction } from 'styles/revampTokens';

// Scoped to the Dimensions "Items" table only (set via cell.isModernDropdown
// by NewTableHelpers.tsx / TableHelpers.tsx / TableHelpersopz.tsx) — this
// component is shared by 8 other tables, so the plain option-list styling
// stays untouched for them; only our table gets this modern look. Matches
// the same recipe as the Rename/Duplicate/Delete menu (DimensionItemTab.tsx)
// exactly — grey MenuList background + 1px gap between white items is what
// creates the divider lines — just without icons, since this is a plain
// option list.
//
// Colors: `#F5F5F5` (hover) and `#BEBEBE` (placeholder) have no exact match
// in `styles/revampTokens.ts` — kept as literals here rather than snapped to
// the nearest token (`neutral.surfaceHover` #F2F3F5 / `text.muted` #98A0AD),
// which would silently shift the rendered colour. `#121826` and `#8B5CF6` do
// match tokens exactly, so those reuse `primaryAction.bg` / `accent.solid`.
export const modernMenuOptionSx = {
  bg: 'white',
  borderRadius: '0 !important',
  padding: '9px 11px',
  fontFamily: 'Roboto Regular',
  fontSize: '13px',
  color: primaryAction.bg,
  _hover: { bg: '#F5F5F5' },
  _focus: { bg: 'white' },
  '&[aria-checked="true"]': { bg: 'white' },
  '.chakra-menu__icon-wrapper': { color: accent.solid },
};

const MODERN_PLACEHOLDER_COLOR = '#BEBEBE';

interface ModernDropdownValueButtonProperties {
  cell: { selectedValue?: string };
  className?: string;
}

/**
 * Modern (Dimensions-table-only) rendering of the dropdown's closed-state
 * button — split out of `DropdownValueButton` so that component's
 * `isRevampTable` / default ternary chain doesn't also have to carry this
 * table's branch.
 */
export const ModernDropdownValueButton = ({
  cell,
  className,
}: ModernDropdownValueButtonProperties) => {
  const hasValue = Boolean(cell?.selectedValue);
  const textColor = hasValue ? primaryAction.bg : MODERN_PLACEHOLDER_COLOR;

  return (
    <MenuButton
      as={Button}
      className={`menu-cell ${className ?? ''}`}
      border='none'
      fontWeight={400}
      textDecoration='none'
      rightIcon={<ChevronDownIcon fontSize='md' />}
      h='100%'
      padding='7px 9px'
      fontSize='13px'
      fontFamily='Roboto Regular'
      color={textColor}
      bg='transparent'
      textAlign='start'
      textTransform='none'
      boxShadow='none'
      _hover={{ bg: 'transparent' }}
      _active={{ bg: 'transparent' }}
    >
      {hasValue ? (
        <Text fontFamily='Roboto Regular' fontWeight={400} color={primaryAction.bg}>
          {capitalizeFirstLetter(cell?.selectedValue ?? '')}
        </Text>
      ) : (
        <Text
          opacity={1}
          fontFamily='Roboto Regular'
          fontWeight={400}
          color={MODERN_PLACEHOLDER_COLOR}
        >
          Select...
        </Text>
      )}
    </MenuButton>
  );
};
